import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { SectionHeading } from "@/components/Brand";
import AdminEvents from "@/components/admin/AdminEvents";
import CreateEventWizard from "@/components/admin/CreateEventWizard";
import AdminMedia from "@/components/admin/AdminMedia";
import AdminUpload from "@/components/admin/AdminUpload";
import AdminVideos from "@/components/admin/AdminVideos";
import AdminSiteImages from "@/components/admin/AdminSiteImages";
import AdminCollectionAlbums from "@/components/admin/AdminCollectionAlbums";
import AdminRestorations from "@/components/admin/AdminRestorations";
import AdminPhotoLab from "@/components/admin/AdminPhotoLab";
import AdminDuplicates from "@/components/admin/AdminDuplicates";
import AdminSubmissions from "@/components/admin/AdminSubmissions";
import AdminUsers from "@/components/admin/AdminUsers";
import AdminPricing from "@/components/admin/AdminPricing";
import AdminManual from "@/components/admin/AdminManual";

const STAT_KEYS = [
  ["total_events", "admin.stats.events"],
  ["total_albums", "admin.stats.albums"],
  ["total_photos", "admin.stats.photos"],
  ["total_videos", "admin.stats.videos"],
  ["registered_users", "admin.stats.users"],
  ["pending_uploads", "admin.stats.pending"],
  ["private_galleries", "admin.stats.private"],
  ["public_galleries", "admin.stats.public"],
  ["storage_used_mb", "admin.stats.storage"],
  ["recent_downloads", "admin.stats.downloads"],
];

const TABS = [
  ["overview", "admin.overview"],
  ["create", "admin.createEvent"],
  ["events", "admin.events"],
  ["upload", "admin.upload"],
  ["videos", "admin.videos"],
  ["site", "admin.siteImages"],
  ["collection", "nav.collection"],
  ["restoration", "nav.restoration"],
  ["photolab", "nav.photolab"],
  ["duplicates", "nav.duplicates"],
  ["media", "admin.media"],
  ["submissions", "admin.submissions"],
  ["users", "admin.users"],
  ["pricing", "admin.pricing"],
  ["manual", "nav.manual"],
];

export default function AdminDashboard() {
  const { t, path } = useI18n();
  const { user, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!loading && !isAdmin) navigate(path("login"), { replace: true });
  }, [loading, isAdmin, navigate, path]);

  useEffect(() => {
    if (isAdmin) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [isAdmin, refresh, tab]);

  if (loading || !user) return <p className="p-12 text-sm text-[#5A5752]">{t("common.loading")}</p>;
  if (!isAdmin) return null;

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-12 sm:px-8 lg:px-12 lg:py-16" data-testid="admin-dashboard">
      <SectionHeading label="HR Creations" title={t("admin.title")} sub={user.email} />

      <div className="mt-8 flex flex-wrap gap-2" role="tablist">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`min-h-[44px] border px-4 text-sm transition-colors duration-300 ${
              tab === key
                ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]"
                : "border-[#C69A3B]/50 text-[#252525] hover:border-[#8F3329]"
            }`}
          >
            {t(label)}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "overview" && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="admin-stats-grid">
            {STAT_KEYS.map(([key, label]) => (
              <div key={key} data-testid={`admin-stat-${key}`} className="border border-[#C69A3B]/30 bg-white p-5">
                <p className="label-mono">{t(label)}</p>
                <p className="mt-3 font-serif-display text-3xl text-[#8F3329]">
                  {stats ? stats[key] ?? 0 : "—"}
                </p>
              </div>
            ))}
          </div>
        )}
        {tab === "create" && <CreateEventWizard onCreated={() => { setRefresh((r) => r + 1); setTab("events"); }} />}
        {tab === "events" && <AdminEvents refreshToken={refresh} onChange={() => setRefresh((r) => r + 1)} />}
        {tab === "upload" && <AdminUpload onUploaded={() => setRefresh((r) => r + 1)} />}
        {tab === "videos" && <AdminVideos onChange={() => setRefresh((r) => r + 1)} />}
        {tab === "site" && <AdminSiteImages />}
        {tab === "collection" && <AdminCollectionAlbums />}
        {tab === "restoration" && <AdminRestorations />}
        {tab === "photolab" && <AdminPhotoLab />}
        {tab === "duplicates" && <AdminDuplicates />}
        {tab === "media" && <AdminMedia refreshToken={refresh} onChange={() => setRefresh((r) => r + 1)} />}
        {tab === "submissions" && <AdminSubmissions onChange={() => setRefresh((r) => r + 1)} />}
        {tab === "users" && <AdminUsers />}
        {tab === "pricing" && <AdminPricing />}
        {tab === "manual" && <AdminManual />}
      </div>
    </div>
  );
}
